function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { toError } from '../../sources/private/parsers/error';
import { initHttp } from '../../utils/http';
import { createMiddleware, get } from '../../utils/utils';
import { sendNewRelicError } from '../new-relic';
import { parseJson } from './middlewares/parse-json';
import { ContentType } from '../../constants/ContentType';
var OPEN_REJECT_TIMEOUT = 10 * 1000;
var REOPEN_TIMEOUT = 30 * 1000;
var defaultSettings = {
  messageUrl: 'https://ws.wixmp.com/message',
  socketUrl: 'wss://ws.wixmp.com/ws',
  tokenUrl: 'https://files.wix.com/users/get-batoken',
  openTimeout: OPEN_REJECT_TIMEOUT
};

function cachePromise(factory) {
  var pending;
  return function () {
    if (pending) {
      return pending;
    }

    pending = factory.apply(this, arguments)["catch"](function (reason) {
      pending = null;
      return Promise.reject(reason);
    });
    return pending;
  };
}

export function createSocket(settings, requestOptions) {
  if (settings === void 0) {
    settings = {};
  }

  settings = _objectSpread({}, defaultSettings, {}, settings);
  var http = initHttp(requestOptions);
  var socket = null;
  var openStarted;
  var openTimeout;
  var socketOpeningPromise;
  var status = 'INIT';
  var events = [];
  var supportedEvents = ['open', 'close', 'message'];
  var messageFlow = createMiddleware([parseJson, function (message) {
    return emit('message', message);
  }]);

  function use(middleware) {
    messageFlow.setMiddlewares([].concat(messageFlow.getMiddlewares().slice(0, -1), [// use all previous middlewares except event emitter
    middleware, function (message) {
      return emit('message', message);
    } // Last middleware should wlway be an event emitter
    ]));
  }

  var getToken = cachePromise(function () {
    return http.get(settings.tokenUrl, null, {
      withCredentials: true
    }).then(function (response) {
      status = 'GOT_TOKEN';
      return get(response, 'data.token', '');
    })["catch"](function (response) {
      status = 'GET_TOKEN_FAILED';
      openStarted = false;
      sendNewRelicError('can not get socket token', {
        code: response.code,
        body: response.responseText
      });
      return toError(response);
    });
  });

  function filterEvents(eventName) {
    return function (event) {
      return event.name === eventName;
    };
  }

  function on(eventNames, callback) {
    [].concat(eventNames).forEach(function (eventName) {
      if (supportedEvents.indexOf(eventName) < 0) {
        throw new Error("This event " + eventName + " is not supported by socket service");
      }

      events.push({
        name: eventName,
        fn: callback
      });
    });
  }

  function off(eventNames, callback) {
    eventNames = [].concat(eventNames);
    events = events.filter(function (event) {
      return !eventNames.some(function (eventName) {
        return event.name === eventName && (!callback || event.fn === callback);
      });
    });
  }

  function emit(eventName, args) {
    var __slice = [].slice;
    args = arguments.length >= 2 ? __slice.call(arguments, 1) : [];
    events.filter(filterEvents(eventName)).forEach(function (event) {
      event.fn.apply({
        event: eventName
      }, args);
    });
  }

  function open() {
    if (socket) {
      return Promise.resolve(socket);
    }

    if (openStarted) {
      return socketOpeningPromise;
    }

    openStarted = true;
    status = 'OPEN_STARTED';
    socketOpeningPromise = getToken().then(openSocket)["catch"](function (reason) {
      sendNewRelicError('can not open socket', {
        code: reason.code,
        body: reason.responseText || reason.message
      });
      return toError(reason);
    });
    return socketOpeningPromise;
  }

  function reopenSocket() {
    if (!openStarted) {
      return;
    }

    status = 'REOPEN_STARTED';
    openTimeout = setTimeout(function () {
      getToken().then(openSocket);
    }, REOPEN_TIMEOUT);
  }

  function openSocket(token) {
    socket = new WebSocket(settings.socketUrl + "?token=" + token);

    socket.onmessage = function (message) {
      return messageFlow.emit(message);
    };

    socket.onerror = function () {
      sendNewRelicError('socket on error');
      reopenSocket();
    };

    socket.onopen = function () {
      status = 'OPENED';
      emit('open');
    };

    socket.onclose = function (closeEvent) {
      socket = null;
      sendNewRelicError('socket on close', {
        code: closeEvent.code,
        message: closeEvent.reason
      });
      emit('close');
    };

    return new Promise(function (resolve, reject) {
      var rejectTimeoutId = setTimeout(function () {
        sendNewRelicError('reject by inner timeout');
        reject({
          code: 0,
          message: 'reject by inner timeout'
        });
      }, settings.openTimeout);
      on('open', function () {
        resolve(socket);
        clearTimeout(rejectTimeoutId);
      });
    });
  }

  function close() {
    openStarted = false;

    if (openTimeout) {
      clearTimeout(openTimeout);
      openTimeout = null;
    }

    if (socket) {
      status = 'CLOSED';
      socket.close();
      return new Promise(function (resolve) {
        on('close', function () {
          resolve('closed');
        });
      });
    }

    sendNewRelicError('socket: no socket to close', {
      message: 'socket: no socket to close'
    });
    return Promise.reject('no socket to close');
  }

  function send(message, action, method) {
    return getToken().then(function (token) {
      if (!token) {
        sendNewRelicError('socket: try to send message without token');
        return Promise.reject('no token to send message');
      }

      if (!message) {
        sendNewRelicError('socket: try to send message without message');
        return Promise.reject('no message to send message');
      }

      var data = {
        action: action || 'send'
      };

      if (method === 'post' || !socket) {
        data = _objectSpread({}, data, {
          message: JSON.stringify(message),
          token: token
        });
        var options = {
          headers: {
            'Content-Type': ContentType.UrlEncoded
          }
        };
        return http.post(settings.messageUrl, data, null, options)["catch"](function (reason) {
          sendNewRelicError('socket: send message failed', {
            code: reason.code,
            body: reason.responseText
          });
          return toError(reason);
        });
      }

      data = _objectSpread({}, data, {
        message: message
      });
      socket.send(JSON.stringify(data));
      return message;
    });
  }

  return {
    open: open,
    close: close,
    send: send,
    on: on,
    off: off,
    use: use,
    getStatus: function getStatus() {
      return status;
    },
    getOpenStarted: function getOpenStarted() {
      return openStarted;
    }
  };
}